import { ReactElement } from 'react';

import { InfoModal, AppIcons } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { FIELD_CONFIGS } from '@backrs_ui/grow-ui/src/pages/Profile/configs/field.config';
import { PAGE_CONFIGS } from '@backrs_ui/grow-ui/src/pages/Profile/configs/section.config';
import { useGetSelectedOptions } from '@backrs_ui/grow-ui/src/pages/Profile/hooks/useGetSelectedOptions';
import { ProfileEditSection } from '@backrs_ui/grow-ui/src/pages/Profile/ProfileEditSection';
import {
  ProfileFieldConfig,
  ProfileFieldValue,
} from '@backrs_ui/grow-ui/src/pages/Profile/types/profile.types';
import {
  determineDefaultValue,
  handleFieldChange,
  handleUpdatingAnswer,
} from '@backrs_ui/grow-ui/src/pages/Profile/utils/fields.helpers';
import {
  ProfileFields,
  ProfileValueOption,
  ProfileValueOptionResponse,
  StoryDocument,
  useFetchUserProfileInfoQuery,
  useUpdateUserProfileMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';

import { Box, SxProps, Theme, Typography, Button } from '@mui/material';

import { assessmentChecklistConfig } from '../hooks/assessmentChecklist.config';

const ASSESSMENT_CHECKLIST_STRINGS = appStrings.modules.assessMyTrack.assessmentChecklist;

function getFeaturedIcon(fieldKeys: ProfileFields[]) {
  const iconMap: Partial<Record<ProfileFields, AppIcons>> = {
    documentsResumes: 'File06',
    documentsTranscripts: 'File06',
    major_gpa: 'Calculator',
    sat_score: 'Calculator',
    act_score: 'Calculator',
    high_school_gpa: 'Calculator',
    post_secondary_gpa: 'Calculator',
    academicsPostSecondaryCurrentEnrollment: 'Building08',
    academicMajors: 'Certificate01',
    academicRigor: 'BookOpen01',
  };

  const icons = fieldKeys.map((key) => iconMap[key] || 'File01');

  // if not all icons are the same for the input field keys, return default
  return icons.every((icon) => icon === icons[0]) ? icons[0] : 'File01';
}

type ChecklistEditModaHeaderProps = Readonly<{
  title: string;
  description?: string;
}>;

const ChecklistEditModaHeader = ({ title, description }: ChecklistEditModaHeaderProps) => {
  return (
    <Box sx={headerContainerSx}>
      <Typography variant="text-lg-semibold">{title}</Typography>
      <Typography variant="text-sm-regular" sx={headerDescriptionSx}>
        {description}
      </Typography>
    </Box>
  );
};

type ChecklistEditModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  profileOptions: ProfileValueOptionResponse;
  fieldKey: ProfileFields | ProfileFields[];
  description?: string;
}>;

export const ChecklistEditModal = ({
  open,
  onClose,
  onSubmit,
  profileOptions,
  fieldKey,
  description = ASSESSMENT_CHECKLIST_STRINGS.thisInformationWillBeUpdatedInYourProfile,
}: ChecklistEditModalProps) => {
  const [updateProfile] = useUpdateUserProfileMutation();

  const { data: profileData, refetch } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);

  const selectedOptions = useGetSelectedOptions({
    userProfile: profileData,
    isEditView: true,
  });

  const allSections = [...PAGE_CONFIGS.Academics.sections, ...PAGE_CONFIGS.Documents.sections];

  // transform fieldKey to an array if it's not already
  const fieldKeys = Array.isArray(fieldKey) ? fieldKey : [fieldKey];

  // find the edit modal(s) for the field keys
  const editModal = assessmentChecklistConfig.find((item) => {
    const itemKeys = Array.isArray(item.fieldKey) ? item.fieldKey : [item.fieldKey];

    return itemKeys.length === fieldKeys.length && itemKeys.every((k, i) => k === fieldKeys[i]);
  })?.editModal;

  const getFieldConfig = (oneFieldKey: ProfileFields) => {
    const config = FIELD_CONFIGS[oneFieldKey] as ProfileFieldConfig;
    const filteredOptions = selectedOptions(oneFieldKey).filter(
      (opt): opt is ProfileValueOption | string => opt !== undefined,
    );
    const options = profileOptions[oneFieldKey];
    const defaultValue = determineDefaultValue({ config, filteredOptions });

    return {
      options,
      defaultValue,
      ...config,
      storyDocuments: selectedOptions(oneFieldKey) as StoryDocument[],
      onChange: (values: ProfileFieldValue[]) => {
        handleFieldChange({
          config,
          values,
          fieldKey: oneFieldKey,
          handleUpdatingAnswer: (key, value) => handleUpdatingAnswer(updateProfile, key, value),
          refetchUserProfile: refetch,
        });
      },
    };
  };

  const getProfileEditComponent = (fieldKeys: ProfileFields[]): ReactElement | null => {
    if (!profileOptions) return null;

    const fields = fieldKeys.map((oneFieldKey) => getFieldConfig(oneFieldKey)).filter(Boolean);

    if (fields.length === 0) return null;

    // find the relevant profile section
    const section = allSections.find((section) =>
      fieldKeys.every((key) => section.keys.includes(key)),
    );

    if (!section) return null;

    return (
      <ProfileEditSection
        {...section}
        description=""
        fields={fields}
        profileOptions={profileOptions}
        isPrivateField={false}
        hideDivider={true}
        hideSectionInfo={true}
      />
    );
  };

  return (
    <InfoModal
      icon={getFeaturedIcon(fieldKeys)}
      headerContent={
        <ChecklistEditModaHeader title={editModal?.title || ''} description={description} />
      }
      open={open}
      onClose={onClose}
      width="xs"
      bodyContent={
        <Box sx={editSectionContainerSx}>
          {getProfileEditComponent(fieldKeys)}
          <Button
            hierarchy="Primary"
            size="md"
            sx={doneButtonSx}
            onClick={onSubmit}
            id="assess-track-checklist-done-button"
          >
            {editModal?.doneButtonText}
          </Button>
        </Box>
      }
    />
  );
};

const editSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  position: 'relative',
  zIndex: 1,
});

const headerContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  position: 'relative',
  zIndex: 1,
};

const headerDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const doneButtonSx: SxProps<Theme> = {
  width: 'fit-content',
  alignSelf: 'flex-end',
};
